import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { useNavigate } from 'react-router-dom';
import { useGetUserID } from '../hooks/useGetUserID';
import { useCookies } from 'react-cookie';
import IngredientInput from '../components/IngredientInput';
import '../styles/IngredientManagement.css';

export default function IngredientManagement() {
    const [cookies] = useCookies(["access_token"]);
    const [ingredients, setIngredients] = useState([]);
    const emptyIngredient = {
        name: '', 
        costPerUnit: '', 
        baseUnit: '', 
        unitEquivalent: { amount: '', unit: '' }, 
        brand: '',
        image: null,
        imageUrl: null,
        special: {
            type: '',
            buyQuantity: '',
            getQuantity: '',
            specialPrice: '',
            savingsAmount: '',
            savingsPercentage: '',
            active: false
        }
    };
    const [newIngredients, setNewIngredients] = useState([{ ...emptyIngredient }]);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const userID = useGetUserID();
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredIngredients, setFilteredIngredients] = useState([]);

    const fetchIngredients = useCallback(async () => {
        if (!cookies.access_token) {
            setError('Please login to view ingredients');
            return;
        }

        try {
            const response = await axios.get(`${API_BASE_URL}/ingredients`, {
                headers: {
                    'Authorization': `Bearer ${cookies.access_token}`,
                    'Content-Type': 'application/json'
                },
                withCredentials: true
            });
            setIngredients(response.data);
        } catch (error) {
            if (error.response?.status === 401) {
                setError('Session expired. Please login again.');
                navigate('/auth');
            } else {
                setError('Error fetching ingredients');
            }
            console.error(error);
        }
    }, [cookies.access_token, navigate]);

    useEffect(() => {
        const storedUsername = localStorage.getItem('username');
        const isPowerUser = localStorage.getItem('isPowerUser') === 'true';
        if (storedUsername !== 'Higgo' || !isPowerUser) {
            navigate('/');
            return;
        }
        fetchIngredients();
    }, [fetchIngredients, navigate]);

    useEffect(() => {
        if (!ingredients || !Array.isArray(ingredients)) return;
        
        const filtered = ingredients.filter(ingredient => {
            // Add null checks for ingredient name and brand
            const name = ingredient?.name?.toLowerCase() || '';
            const brand = ingredient?.brand?.toLowerCase() || '';
            const search = searchTerm?.toLowerCase() || '';
            
            return name.includes(search) || brand.includes(search);
        });
        
        setFilteredIngredients(filtered);
    }, [ingredients, searchTerm]);

    const handleIngredientChange = (index, field, value) => {
        const updatedIngredients = [...newIngredients];
        if (field.startsWith('special.')) {
            const [parent, child] = field.split('.');
            updatedIngredients[index][parent] = {
                ...updatedIngredients[index][parent],
                [child]: value,
                active: true
            };
        } else if (field.startsWith('unitEquivalent.')) {
            const [parent, child] = field.split('.');
            updatedIngredients[index][parent] = {
                ...updatedIngredients[index][parent],
                [child]: value
            };
        } else {
            updatedIngredients[index][field] = value;
        }
        setNewIngredients(updatedIngredients);
    };

    const handleAddIngredient = () => {
        setNewIngredients([...newIngredients, { ...emptyIngredient }]);
    };

    const handleRemoveIngredient = (index) => {
        const updatedIngredients = newIngredients.filter((_, i) => i !== index);
        setNewIngredients(updatedIngredients);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!cookies.access_token) {
            setError('Please login to add ingredients');
            return;
        }

        try {
            const headers = {
                'Authorization': `Bearer ${cookies.access_token}`
            };

            for (const ingredient of newIngredients) {
                // Validate required fields
                if (!ingredient.name || !ingredient.costPerUnit || !ingredient.baseUnit || !ingredient.brand) {
                    setError('Please fill in all required fields for all ingredients');
                    return;
                }

                // Create FormData for multipart/form-data
                const formData = new FormData();
                formData.append('name', ingredient.name);
                formData.append('costPerUnit', ingredient.costPerUnit);
                formData.append('baseUnit', ingredient.baseUnit);
                formData.append('brand', ingredient.brand);
                
                if (ingredient.image) {
                    formData.append('image', ingredient.image);
                }

                if (ingredient.unitEquivalent?.amount) {
                    formData.append('unitEquivalent[amount]', ingredient.unitEquivalent.amount);
                    formData.append('unitEquivalent[unit]', ingredient.baseUnit);
                }

                if (ingredient.special?.active) {
                    formData.append('special[type]', ingredient.special.type);
                    formData.append('special[active]', true);
                    
                    switch (ingredient.special.type) {
                        case 'MULTI_BUY':
                            formData.append('special[buyQuantity]', ingredient.special.buyQuantity);
                            formData.append('special[specialPrice]', ingredient.special.specialPrice);
                            break;
                        case 'PERCENTAGE_OFF':
                            formData.append('special[savingsPercentage]', ingredient.special.savingsPercentage);
                            break;
                        case 'FIXED_PRICE':
                            formData.append('special[specialPrice]', ingredient.special.specialPrice);
                            break;
                        case 'BUY_X_GET_Y':
                            formData.append('special[buyQuantity]', ingredient.special.buyQuantity);
                            formData.append('special[getQuantity]', ingredient.special.getQuantity);
                            break;
                    }
                }

                // Make the API call to save each ingredient
                await axios.post(`${API_BASE_URL}/ingredients`, formData, {
                    headers: {
                        ...headers,
                        'Content-Type': 'multipart/form-data'
                    }
                });
            }

            // Force a complete reset of the form including images
            const freshEmptyIngredient = {
                name: '', 
                costPerUnit: '', 
                baseUnit: '', 
                unitEquivalent: { amount: '', unit: '' }, 
                brand: '',
                image: null,
                imageUrl: null,
                special: {
                    type: '',
                    buyQuantity: '',
                    getQuantity: '',
                    specialPrice: '',
                    savingsAmount: '',
                    savingsPercentage: '',
                    active: false
                }
            };

            // Clear the form completely
            setNewIngredients([freshEmptyIngredient]);
            
            // Force re-render by setting state to empty and then to fresh state
            setNewIngredients([]);
            setTimeout(() => {
                setNewIngredients([freshEmptyIngredient]);
            }, 0);

            // Fetch updated ingredients list
            await fetchIngredients();

        } catch (error) {
            if (error.response?.status === 401) {
                setError('Session expired. Please login again.');
                navigate('/auth');
            } else {
                setError(error.response?.data?.message || 'Error saving ingredients');
            }
            console.error(error);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this ingredient?')) {
            return;
        }

        try {
            await axios.delete(`${API_BASE_URL}/ingredients/${id}`, {
                headers: {
                    Authorization: `Bearer ${cookies.access_token}`
                }
            });
            
            // Refresh the ingredients list
            fetchIngredients();
            setError('');
        } catch (error) {
            console.error('Error deleting ingredient:', error);
            setError(error.response?.data?.message || 'Error deleting ingredient');
        }
    };

    const handleEdit = async (ingredient) => {
        try {
            // Ensure all necessary data is included
            const ingredientData = {
                _id: ingredient._id,
                name: ingredient.name,
                costPerUnit: ingredient.costPerUnit,
                baseUnit: ingredient.baseUnit,
                unitEquivalent: ingredient.unitEquivalent,
                brand: ingredient.brand,
                special: ingredient.special
            };
            
            navigate(`/edit-ingredient/${ingredient._id}`, { 
                state: { ingredient: ingredientData } 
            });
        } catch (error) {
            console.error('Error navigating to edit:', error);
            setError('Error preparing to edit ingredient');
        }
    };

    const calculateSpecialSavings = (ingredient) => {
        if (!ingredient.special?.active) return null;

        const { special, costPerUnit } = ingredient;
        switch (special.type) {
            case 'MULTI_BUY':
                const normalPrice = special.buyQuantity * costPerUnit;
                const savings = normalPrice - special.specialPrice;
                const savingsPercent = ((savings / normalPrice) * 100).toFixed(1);
                return {
                    amount: savings.toFixed(2),
                    percent: savingsPercent
                };
            case 'PERCENTAGE_OFF':
                const discountAmount = costPerUnit * (special.savingsPercentage / 100);
                return {
                    amount: discountAmount.toFixed(2),
                    percent: special.savingsPercentage,
                    originalPrice: costPerUnit,
                    finalPrice: (costPerUnit - discountAmount).toFixed(2)
                };
            default:
                return null;
        }
    };

    return (
        <div className="ingredient-management-container">
            <h2>Ingredient Management</h2>
            
            <div className="management-links">
                <button
                    className="categories-link"
                    onClick={() => navigate('/ingredient-categories')}
                >
                    Manage Ingredient Categories
                </button>
            </div>
            
            {error && <div className="error-message">{error}</div>}
            
            <div className="search-container">
                <input
                    type="text"
                    placeholder="Search ingredients..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="ingredient-search"
                />
            </div>

            <form onSubmit={handleSubmit} className="ingredient-form">
                {newIngredients.map((ingredient, index) => (
                    <IngredientInput
                        key={index}
                        ingredient={ingredient}
                        index={index}
                        handleIngredientChange={handleIngredientChange}
                        removeIngredient={handleRemoveIngredient}
                    />
                ))}
                <button type="button" onClick={handleAddIngredient} className="add-ingredient-button">
                    Add Ingredient
                </button>
                <button type="submit" className="submit-button">
                    Save Ingredients
                </button>
            </form>

            <div className="ingredients-list">
                <h2>Existing Ingredients</h2>
                <table>
                    <thead>
                        <tr>
                            <th>Image</th>
                            <th>Name</th>
                            <th>Cost Per Unit</th>
                            <th>Base Unit</th>
                            <th>Unit Equivalent</th>
                            <th>Brand</th>
                            <th>Special</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredIngredients.map((ingredient) => {
                            const savings = calculateSpecialSavings(ingredient);
                            return (
                                <tr key={ingredient._id} className={ingredient.special?.active ? 'has-special' : ''}>
                                    <td>
                                        {ingredient.imageUrl ? (
                                            <img 
                                                src={ingredient.imageUrl} 
                                                alt={ingredient.name}
                                                style={{
                                                    width: '50px',
                                                    height: '50px',
                                                    objectFit: 'cover',
                                                    borderRadius: '4px'
                                                }}
                                            />
                                        ) : (
                                            <div 
                                                style={{
                                                    width: '50px',
                                                    height: '50px',
                                                    backgroundColor: '#f0f0f0',
                                                    borderRadius: '4px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: '#999'
                                                }}
                                            >
                                                No image
                                            </div>
                                        )}
                                    </td>
                                    <td>{ingredient.name}</td>
                                    <td>R{ingredient.costPerUnit}</td>
                                    <td>{ingredient.baseUnit}</td>
                                    <td>
                                        {ingredient.unitEquivalent?.amount 
                                            ? `${ingredient.unitEquivalent.amount}${ingredient.unitEquivalent.unit}`
                                            : '-'
                                        }
                                    </td>
                                    <td>{ingredient.brand || '-'}</td>
                                    <td>
                                        {ingredient.special?.active ? (
                                            <div className="special-info">
                                                <span className="special-badge">Special!</span>
                                                <p className="special-type">
                                                    {ingredient.special.type === 'MULTI_BUY' && (
                                                        `Buy ${ingredient.special.buyQuantity} for R${ingredient.special.specialPrice}`
                                                    )}
                                                    {ingredient.special.type === 'PERCENTAGE_OFF' && (
                                                        <>
                                                            {`${ingredient.special.savingsPercentage}% off`}
                                                            {savings && (
                                                                <span className="original-price">
                                                                    Was R{savings.originalPrice}
                                                                </span>
                                                            )}
                                                        </>
                                                    )}
                                                </p>
                                                {savings && (
                                                    <p className="savings-info">
                                                        {savings.amount && `Save: R${savings.amount}`}
                                                        {savings.finalPrice && ` Now: R${savings.finalPrice}`}
                                                        {savings.percent && ` (${savings.percent}%)`}
                                                    </p>
                                                )}
                                            </div>
                                        ) : '-'}
                                    </td>
                                    <td>
                                        <button 
                                            onClick={() => handleEdit(ingredient)}
                                            className="edit-button"
                                        >
                                            Edit
                                        </button>
                                        <button 
                                            onClick={() => handleDelete(ingredient._id)}
                                            className="delete-button"
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <style>
            {`
                .search-container {
                    margin: 20px 0;
                }

                .search-input {
                    width: 100%;
                    padding: 10px;
                    border: 1px solid #ddd;
                    border-radius: 4px;
                    font-size: 16px;
                }

                .special-info {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                }

                .special-type {
                    color: #2c3e50;
                    font-weight: 500;
                    margin: 0;
                }

                .savings-info {
                    color: #e74c3c;
                    font-size: 0.9em;
                    margin: 0;
                }

                .has-special {
                    background-color: #fff3f3;
                }

                .original-price {
                    text-decoration: line-through;
                    color: #666;
                    margin-left: 8px;
                    font-size: 0.9em;
                }
            `}
            </style>
        </div>
    );
}
